import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Input, Radio, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import toast from 'react-hot-toast';
import {PrinterIcon} from "@heroicons/react/24/solid";
import {HiCreditCard} from "react-icons/hi2";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";

const statusTones = {
    new: 'blue',
    queue: 'amber',
    pending: 'orange',
    examined: 'green',
    completed: 'slate',
    canceled: 'red',
};

const Admissions = () => {
    const { t } = useTranslation();
    const sum = (value) => `${value} ${t('common.currency')}`;
    const [admissions, setAdmissions] = useState([]);
    const [filteredAdmissions, setFilteredAdmissions] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState({});
    const [sorter, setSorter] = useState({ field: 'id_visit', order: 'descend' });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentType, setPaymentType] = useState('cash');
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [paymentReceipt, setPaymentReceipt] = useState(null);
    const [selectedVisit, setSelectedVisit] = useState(null); // Добавлено для отслеживания выбранного визита

    const fetchAdmissions = async (page = 1, pageSize = 10, sortField = 'id_visit', sortOrder = 'descend') => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/visit?page=${page}&size=${pageSize}&status[0]=new`);
            const admissionsData = response.data.data.map((item, index) => {
                const filteredOrders = item.orders.filter(order => order.service_type && !order.payed);
                return {
                    key: index + 1,
                    id_visit: item.id,
                    patient_name: item.patient_id.name,
                    doctor_name: item.doctor.name,
                    total_amount: item.total_amount,
                    total_payed: item.total_payed,
                    total_debit: item.total_debit,
                    date_at: item.date_at,
                    status: item.status,
                    orders: filteredOrders,
                    children_amount: item.children_amount, // Добавляем необходимые поля
                    children_payed: item.children_payed,
                    children_debit: item.children_debit
                };
            });
            const total = response.data.meta.total;

            const sortedData = admissionsData.sort((a, b) => {
                if (sortField && sortOrder) {
                    const orderMultiplier = sortOrder === 'ascend' ? 1 : -1;
                    if (sortField === 'id_visit' || sortField === 'key') {
                        return (a[sortField] - b[sortField]) * orderMultiplier;
                    }
                    return a[sortField].localeCompare(b[sortField]) * orderMultiplier;
                }
                return 0;
            });

            setAdmissions(sortedData);
            setFilteredAdmissions(sortedData);
            setPagination({ ...pagination, current: page, pageSize, total });
        } catch (error) {
            console.error("Error fetching admissions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmissions();
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        fetchAdmissions(pagination.current, pagination.pageSize, sorter.field, sorter.order);
        setFilters(filters);
        setSorter(sorter);
    };

    const getStatusName = (status) => t(`status.${status}`, { defaultValue: status });

    const paymentTypeNames = {
        cash: t('payments.cash'),
        credit: t('payments.credit'),
        card: t('payments.creditCard')
    };

    const paymentBillNames = {
        pending: t('status.pending'),
        payed: t('status.payed'),
    };

    const handlePaymentClick = (record) => {
        const availableOrder = record.orders.find(order => !order.payed);
        if (availableOrder) {
            setSelectedOrder(availableOrder);
            setSelectedVisit(record);
            setIsModalVisible(true);
        } else {
            toast.error(t('payments.noOrders'));
        }
    };

    const handlePayment = async () => {
        if (selectedOrder) {
            const orderId = selectedOrder.id;
            const paymentUrl = `/visit/pay/${orderId}`;
            try {
                const response = await axiosInstance.post(paymentUrl, {
                    amount: paymentAmount,
                    type: paymentType
                });
                console.log('Оплата выполнена успешно:', response.data);
                const remainingAmount = parseFloat(selectedOrder.total_amount) - parseFloat(paymentAmount); // Исправлено на selectedOrder.total_amount
                setPaymentReceipt({
                    service: selectedOrder.service.name,
                    amount: paymentAmount,
                    total_amount: selectedOrder.total_amount, // Выводим total_amount из selectedOrder
                    remaining: remainingAmount,
                    type: paymentType,
                    bill: 'payed'
                });

                fetchAdmissions();
                setPaymentAmount('');
                setPaymentType('cash');
                toast.success(t('payments.paySuccess', { id: orderId }));
            } catch (error) {
                console.error('Ошибка при выполнении оплаты:', error);
                toast.error(t('payments.payError'));
            }
        } else {
            console.error('Выбранный заказ отсутствует для оплаты');
        }
    };


    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedOrder(null);
        setPaymentReceipt(null);
        setSelectedVisit(null); // Очищаем выбранный визит
    };


    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'key',
            key: 'id',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            sortOrder: sorter.field === 'key' && sorter.order,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: 'patient_name',
            key: 'patient_name',
            sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
            sortOrder: sorter.field === 'patient_name' && sorter.order,
            render: (text) => (
                <span className="block text-sm font-medium text-[#1D2C7B] min-w-[150px]">{text}</span>
            ),
        },
        {
            title: t('common.doctor'),
            dataIndex: 'doctor_name',
            key: 'doctor_name',
            sorter: (a, b) => a.doctor_name.localeCompare(b.doctor_name),
            sortOrder: sorter.field === 'doctor_name' && sorter.order,
            render: (text) => <span className="text-sm text-slate-700">{text}</span>,
        },
        {
            title: t('payments.total'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 140,
            sorter: (a, b) => a.total_amount - b.total_amount,
            sortOrder: sorter.field === 'total_amount' && sorter.order,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            )
        },
        {
            title: t('payments.paid'),
            dataIndex: 'total_payed',
            key: 'total_payed',
            width: 140,
            sorter: (a, b) => a.total_payed - b.total_payed,
            sortOrder: sorter.field === 'total_payed' && sorter.order,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            )
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortOrder: sorter.field === 'status' && sorter.order,
            render: (text) => (
                <StatusBadge tone={statusTones[text] ?? 'muted'}>{getStatusName(text)}</StatusBadge>
            ),
        },
        {
            title: t('admissions.payment'),
            key: 'payment',
            width: 128,
            fixed: 'right',
            render: (_, record) => (
                <ActionButton
                    icon={<HiCreditCard size="18"/>}
                    collapseOnMobile={false}
                    className="!h-8 !px-2.5 sm:!px-2.5 !text-xs"
                    onClick={() => handlePaymentClick(record)}
                >
                    {t('debtors.pay')}
                </ActionButton>
            ),
        },
    ]
    ;

    const getRowClassName = (record) => {
        if (record.orders.some(order => parseFloat(order.payed) > 0)) {
            return 'disabled-row';
        }
        return '';
    };

    const handleSearch = (value) => {
        setSearchText(value);
        if (value) {
            const filteredData = admissions.filter((admission) =>
                admission.patient_name.toLowerCase().includes(value.toLowerCase()) ||
                admission.doctor_name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredAdmissions(filteredData);
        } else {
            setFilteredAdmissions(admissions);
        }
    };

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B] truncate">
                        {record.patient_name}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">{record.doctor_name}</span>
                </div>
                <RowIconButton onClick={() => handlePaymentClick(record)} aria-label={t('debtors.pay')}>
                    <HiCreditCard size="18"/>
                </RowIconButton>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <StatusBadge tone={statusTones[record.status] ?? 'muted'}>
                    {getStatusName(record.status)}
                </StatusBadge>
                <span className="tabular-nums">{sum(record.total_amount)}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('admissions.queue')}
                    count={pagination.total}
                    filters={
                        <Input.Search
                            placeholder={t('common.searchName')}
                            allowClear
                            size="small"
                            enterButton={t('payments.searchBtn')}
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="md:!w-[260px]"
                        />
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={filteredAdmissions}
                    mobileCard={renderMobileCard}
                    rowKey="key"
                    scrollX={1100}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    rowClassName={getRowClassName}
                />
            </PageCard>

            <Modal centered
                   title={t('admissions.paymentReceipt')}
                   visible={isModalVisible}
                   onCancel={handleModalClose}
                   width="100%"
                   style={{ maxWidth: 520 }}
                   footer={[
                       <Button key="cancel" onClick={handleModalClose}>
                           {t('common.cancel')}
                       </Button>,
                       paymentReceipt && (
                           <Button
                               icon={<PrinterIcon className="w-4 h-4 inline-flex items-center" />}
                               key="print"
                               type="primary"
                               onClick={() => {
                                   const printContent = document.getElementById('printable-receipt').innerHTML;
                                   const newWindow = window.open('', '_blank', 'width=600,height=700');
                                   newWindow.document.write(`
                        <html>
                        <head>
                            <title>${t('payments.receipt')}</title>
                            <style>
                                body { font-family: Arial, sans-serif; }
                                .receipt-container { max-width: 100%; margin: auto; }
                                .text-center { text-align: center; }
                                .divider { border-bottom: 1px dashed #ccc; margin: 0px 0; }
                                @media print {
                                    body { font-size: 9px; }
                                    .receipt-container { max-width: 100%; }
                                    .text-center { font-size: 11px; }
                                    .divider { margin: 0px 0; }
                                }
                            </style>
                        </head>
                        <body>${printContent}</body>
                        </html>
                    `);
                                   newWindow.document.close();
                                   newWindow.focus();
                                   newWindow.print();
                                   newWindow.close();
                               }}
                           >
                               {t('admissions.printReceipt')}
                           </Button>
                       ),
                       !paymentReceipt && (
                           <Button key="submit" type="primary" onClick={handlePayment}>
                               {t('payments.pay')}
                           </Button>
                       ),
                   ]}
            >
                {paymentReceipt ? (
                    <div id="printable-receipt">
                        <div className="w-full max-w-sm mx-auto bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
                            {/* Header with Shop Logo and Name */}
                            <div className="text-center mb-4">
                                <Typography.Title level={5} style={{fontWeight: 'bold'}} className="uppercase">
                                    OXU MED DENTAL
                                </Typography.Title>
                                <Typography.Text className="text-slate-500">
                                    ул. Самарканд 86, г. Бухара
                                </Typography.Text>
                            </div>

                            <Divider dashed className="border-slate-200"/>

                            {/* Services or Products */}
                            <div className="mb-3 text-sm text-slate-700">{paymentReceipt.service}</div>

                            <hr className="border-slate-200" />

                            {/* Summary */}
                            <div className="mb-2 mt-2 text-slate-700">
                                <div className="flex justify-between gap-3">
                                    <Typography.Text strong>{t('payments.total')}:</Typography.Text>
                                    <Typography.Text className="tabular-nums">{sum(selectedOrder.amount)}</Typography.Text>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <Typography.Text strong>{t('payments.paid')}:</Typography.Text>
                                    <Typography.Text className="tabular-nums">{sum(paymentReceipt.amount)}</Typography.Text>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <Typography.Text strong>{t('payments.remaining')}:</Typography.Text>
                                    <Typography.Text className="tabular-nums">
                                        {sum(parseFloat(selectedOrder.amount) - parseFloat(paymentReceipt.amount))}
                                    </Typography.Text>
                                </div>
                            </div>

                            <Divider dashed className="border-slate-200" />

                            {/* Payment Information */}
                            <div className="mb-4 text-slate-700">
                                <div className="flex justify-between gap-3">
                                    <Typography.Text>{t('payments.paymentMethod')}:</Typography.Text>
                                    <Typography.Text>{paymentTypeNames[paymentReceipt.type]}</Typography.Text>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <Typography.Text>{t('payments.paymentStatus')}:</Typography.Text>
                                    <Typography.Text>{paymentBillNames[paymentReceipt.bill]}</Typography.Text>
                                </div>
                            </div>

                            <Divider dashed className="border-slate-200" />
                            <img src="/qr.svg" alt="" className="mx-auto mt-2" width="140" height="140" />

                            {/* Footer */}
                            <div className="text-center">
                                <Typography.Text className="text-slate-500">{t('payments.thankYou')}</Typography.Text>
                                <br />
                                <Typography.Text className="text-slate-500">Тел: +998 33 135 21 01</Typography.Text>
                                <br />
                                <Typography.Text className="text-slate-500">front.geolink.uz</Typography.Text>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        {selectedOrder ? (
                            <div>
                                <Typography.Title level={5}>{t('payments.selectedOrder')}</Typography.Title>
                                <p className="text-sm text-slate-700">{t('common.service')}: {selectedOrder.service.name}</p>
                                <p className="text-sm text-slate-700 tabular-nums">{t('common.amount')}: {sum(selectedOrder.amount)}</p>
                                <Input
                                    type="number"
                                    placeholder={t('payments.enterAmount')}
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                />
                                <Radio.Group
                                    value={paymentType}
                                    onChange={(e) => setPaymentType(e.target.value)}
                                    style={{ marginTop: 10 }}
                                >
                                    <Radio.Button value="cash">{t('payments.cash')}</Radio.Button>
                                    <Radio.Button value="card">{t('payments.creditCard')}</Radio.Button>
                                </Radio.Group>
                            </div>
                        ) : (
                            <p>{t('payments.noOrders')}</p>
                        )}
                    </div>
                )}
            </Modal>

        </PageShell>
    );
};

export default Admissions;
